import { createAuthClient } from 'better-auth/client'
import { createInboxClient } from 'inbox-sdk/client'
import type { Account, Connection, Mailbox, MailboxConversation, MailboxMessageSummary, Policy } from 'inbox-sdk/types'

export type { Account, Connection, Mailbox, MailboxConversation, MailboxMessageSummary, Policy }

export const auth = createAuthClient({ baseURL: location.origin, basePath: '/api/auth' })
export const inbox = createInboxClient({ baseUrl: '/api/inbox', cacheScope: 'session' })

export interface User { id: string; email: string; name: string }
export interface ServerInfo { signups: boolean; googleSignIn: boolean; writes: boolean; triage: boolean; tune?: boolean; complete?: boolean; askSearch?: boolean }

export type Brand = 'gmail' | 'outlook' | 'icloud' | 'fastmail' | 'yahoo' | 'imap' | 'test'
export interface ProviderCard {
  id: string
  name: string
  brand: Brand
  kind: 'oauth' | 'password'
  summary: string
  ready: boolean
  unavailable?: string
  actionLabel: string
  fields?: { name: string; label: string; type: 'email' | 'password' | 'text'; placeholder?: string; autocomplete?: string }[]
  help?: { text: string; url: string; linkLabel: string }
  alternative?: { card: string; label: string }
}
export type BrandedConnection = Connection & { brand: Brand }

export interface Decision {
  id: string
  important: boolean
  needsReply: boolean
  category: string
  label: string
  sender: 'person' | 'company' | 'automated'
  urgency: number
  importance: number
  priority: number
  reason: string
  critical: boolean
  ruleId?: string
}
export interface Rule { id: string; kind: 'text' | 'sender' | 'domain' | 'age'; pattern: string; action: 'other' | 'important'; label: string; createdAt: string; learned?: boolean; evidence?: string }
export interface Habit { received: number; opened: number; replied: number; ignored: number }
export interface Suggestion { ruleId?: string; id: string; kind: 'text' | 'sender' | 'domain'; pattern: string; action: 'important' | 'other'; label: string; evidence: string; confidence: number; createdAt: string }
export interface TriageState { enabled: boolean; pending: number; classified: number; items: Decision[]; rules: Rule[]; done?: string[]; habits?: Record<string, Habit>; suggestions?: Suggestion[]; learned?: Suggestion[] }

export class ApiFailure extends Error {
  constructor(message: string, readonly code: string, readonly status: number) { super(message) }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...init?.headers } })
  const body = await response.json().catch(() => null) as { error?: { code: string; message: string } } | null
  if (!response.ok) throw new ApiFailure(body?.error?.message ?? 'Something went wrong.', body?.error?.code ?? 'HTTP', response.status)
  return body as T
}

export const session = () => api<{ user: User | null; server: ServerInfo }>('/api/session')
export const providers = () => api<{ cards: ProviderCard[]; alternatives: ProviderCard[]; connections: BrandedConnection[] }>('/api/providers')
export const connect = (card: string, fields: Record<string, string> = {}) =>
  api<{ connectionId?: string; authorizeUrl?: string }>('/api/connect', { method: 'POST', body: JSON.stringify({ card, fields }) })
export const triage = () => api<TriageState>('/api/triage')
export const addRule = (input: Pick<Rule, 'kind' | 'pattern' | 'action'> & { label?: string }) =>
  api<{ rule: Rule }>('/api/rules', { method: 'POST', body: JSON.stringify(input) })
export const removeRule = (id: string) => api<{ ok: true }>(`/api/rules/${encodeURIComponent(id)}`, { method: 'DELETE' })

/** A message-level error message for the person, never a raw provider error. */
export function failureText(error: unknown): string {
  if (error instanceof ApiFailure) return error.message
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.length < 200) return error.message
  return 'Something went wrong. Try again.'
}
